import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import authService from '../services/auth.service';
import { getAccessToken, getRefreshToken, clearTokens } from 'services/api';
import { decodeAccessToken, type DecodedUser } from '../utils/jwt';
import { useReadyState } from 'contexts/ReadyStateContext';
import type { AuthState, AuthContextType, LoginCredentials } from '../types';
import logger from 'utils/logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { markAppReady } = useReadyState();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isInitializing: true,
    error: null,
  });

  const setUser = useCallback((decodedUser: DecodedUser | null) => {
    if (decodedUser) {
      setState((prev) => ({
        ...prev,
        user: {
          id: decodedUser.id,
          role: decodedUser.role,
        },
        isAuthenticated: true,
        error: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        user: null,
        isAuthenticated: false,
      }));
    }
  }, []);

  const initializeAuth = useCallback(async () => {
    try {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (!accessToken || !refreshToken) {
        // No tokens found, not authenticated
        setState((prev) => ({ ...prev, isInitializing: false }));
        markAppReady();
        return;
      }

      // Decode access token to get user info
      const decodedUser = decodeAccessToken(accessToken);

      if (decodedUser) {
        // Valid token, set user
        setUser(decodedUser);
        setState((prev) => ({ ...prev, isInitializing: false }));
        markAppReady();
      } else {
        // Access token invalid/expired, try to refresh
        try {
          const newAccessToken = await authService.refreshAccessToken();
          const newDecodedUser = decodeAccessToken(newAccessToken);

          if (newDecodedUser) {
            setUser(newDecodedUser);
          } else {
            clearTokens();
          }

          setState((prev) => ({ ...prev, isInitializing: false }));
          markAppReady();
        } catch (refreshError) {
          const err = refreshError as { response?: { status?: number }; code?: string };
          const status = err.response?.status;

          // Only clear tokens on 4xx errors (auth failures)
          // For 5xx errors, keep tokens and show error state
          if (status && status >= 400 && status < 500) {
            logger.error('Token refresh failed on init (auth error):', refreshError);
            clearTokens();
            setState((prev) => ({ ...prev, isInitializing: false }));
          } else if (status && status >= 500) {
            // 5xx server error - keep user logged in, show error
            logger.error('Token refresh failed on init (server error):', refreshError);
            setState((prev) => ({
              ...prev,
              isInitializing: false,
              error: 'Server error occurred. Please try again later.',
            }));
          } else {
            // Network error (no response) - keep user logged in, show error
            logger.error('Token refresh failed on init (network error):', refreshError);
            setState((prev) => ({
              ...prev,
              isInitializing: false,
              error: 'Unable to connect to server. Please check your internet connection.',
            }));
          }
          markAppReady();
        }
      }
    } catch (error) {
      logger.error('Failed to initialize auth:', error);
      clearTokens();
      setState((prev) => ({
        ...prev,
        isInitializing: false,
        error: 'Failed to initialize authentication',
      }));
      markAppReady();
    }
  }, [setUser, markAppReady]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await authService.login(credentials);

        // Decode access token to extract user info
        const decodedUser = decodeAccessToken(response.access_token);

        if (decodedUser) {
          setUser(decodedUser);
        } else {
          throw new Error('Failed to decode access token');
        }

        setState((prev) => ({ ...prev, isLoading: false }));
      } catch (error: unknown) {
        const err = error as { response?: { data?: { detail?: string } }; message?: string };
        const errorMessage = err.response?.data?.detail || err.message || 'Login failed';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          isAuthenticated: false,
          user: null,
        }));
        clearTokens();
        throw error;
      }
    },
    [setUser]
  );

  const logout = useCallback((): void => {
    authService.logout();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: false,
      error: null,
    });
  }, []);

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const newAccessToken = await authService.refreshAccessToken();

      // Decode new access token to update user info
      const decodedUser = decodeAccessToken(newAccessToken);

      if (decodedUser) {
        setUser(decodedUser);
      }
    } catch (error) {
      const err = error as { response?: { status?: number }; code?: string };
      const status = err.response?.status;

      // Only logout on 4xx errors (auth failures)
      // For 5xx errors, keep user logged in and show error state
      if (status && status >= 400 && status < 500) {
        logger.error('Failed to refresh token (auth error):', error);
        logout();
      } else if (status && status >= 500) {
        // 5xx server error - keep user logged in, show error
        logger.error('Failed to refresh token (server error):', error);
        setState((prev) => ({
          ...prev,
          error: 'Server error occurred. Please try again later.',
        }));
      } else {
        // Network error (no response) - keep user logged in, show error
        logger.error('Failed to refresh token (network error):', error);
        setState((prev) => ({
          ...prev,
          error: 'Unable to connect to server. Please check your internet connection.',
        }));
      }
      throw error;
    }
  }, [setUser, logout]);

  const clearError = useCallback((): void => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextType = useMemo(
    () => ({
      state,
      login,
      logout,
      refreshToken,
      clearError,
    }),
    [state, login, logout, refreshToken, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
